import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { setStyleProperty as _$setStyleProperty } from "solid-js/web";
import { use as _$use } from "solid-js/web";
import { className as _$className } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<span class=tzr-sort>`),
  _tmpl$2 = /*#__PURE__*/_$template(`<th>`),
  _tmpl$3 = /*#__PURE__*/_$template(`<button type=button class=tzr-btn>`),
  _tmpl$4 = /*#__PURE__*/_$template(`<button type=button class="tzr-btn primary">`),
  _tmpl$5 = /*#__PURE__*/_$template(`<div class=tzr-error>`),
  _tmpl$6 = /*#__PURE__*/_$template(`<tr class=tzr-spacer><td colspan=11 style=padding:0;border:none>`),
  _tmpl$7 = /*#__PURE__*/_$template(`<div class=tzr-empty>`),
  _tmpl$8 = /*#__PURE__*/_$template(`<div class=tzr-loading><span class=tzr-spinner>`),
  _tmpl$9 = /*#__PURE__*/_$template(`<div class=tzr-screen><div class=tzr-toolbar><div class=tzr-title></div><div class=tzr-filters><input class=tzr-search></div><button type=button class=tzr-btn></button></div><div class=tzr-body><div class=tzr-table-wrap><table class=tzr-table><thead><tr><th></th></tr></thead><tbody></tbody></table></div></div><div class=tzr-statusbar>–<!> / `),
  _tmpl$0 = /*#__PURE__*/_$template(`<button type=button class=tzr-chip>`),
  _tmpl$1 = /*#__PURE__*/_$template(`<img class=tzr-thumb alt>`),
  _tmpl$10 = /*#__PURE__*/_$template(`<tr><td class=tzr-thumb-cell></td><td class="tzr-right tzr-mono tzr-dim tzr-col-dt"></td><td class="tzr-lp tzr-lp-col"></td><td class="tzr-right tzr-mono tzr-col-speed"></td><td></td><td></td><td></td><td></td><td class=tzr-id></td><td class="tzr-right tzr-mono tzr-dim tzr-col-dt"></td><td><span>`);
/**
 * The office screen: toolbar (state chips, search, filters, import buttons), live record
 * table with infinite scroll, and the detail drawer. Table design follows bosy's meleTable
 * (sticky header, hover rows, selection inset bar); filtering is SERVER-side through the
 * module's filter system, the search box is a quick client-side sieve over the loaded rows.
 */

import { For, Show, createEffect, createSignal } from 'solid-js';
import { useWorkflow } from 'mmodSolidWorkflow';
import { Drawer } from 'mmodSolidModule/components';
import { fmtDtShort } from 'mmodSolidModule/datetime';
import { STATE_TONES } from './constants';
import { ReadPanel } from './drawer';
import { FilterButton, FilterChips } from './filters';

/** Sortable header cell — arrow on the active column, click toggles (server-side sort). */
function Th(props) {
  const {
    store,
    module: m,
    tr
  } = useWorkflow();
  return (() => {
    var _el$ = _tmpl$2();
    _el$.$$click = () => m.toggleSort(props.key_);
    _$insert(_el$, () => tr(props.label || props.key_), null);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return store.sort.key === props.key_;
      },
      get children() {
        var _el$2 = _tmpl$();
        _$insert(_el$2, () => store.sort.dir > 0 ? '▲' : '▼');
        return _el$2;
      }
    }), null);
    _$effect(() => _$className(_el$, props.class || ''));
    return _el$;
  })();
}
const ROW_H = 40; // fixed row height — what makes the DOM virtualization trivial

export function OfficeScreen() {
  const {
    store,
    setStore,
    module: m,
    t,
    tr,
    wf,
    client
  } = useWorkflow();
  const [scroll, setScroll] = createSignal({
    top: 0,
    height: 600
  });
  let elWrap;

  // quick client-side sieve over the held window (the server already applied the filters);
  // the local order mirrors the server sort, so pushed updates land in the right position
  const filtered = () => {
    const needle = store.search.toLowerCase();
    let list = wf().list;
    if (needle) {
      list = list.filter(record => `${record.lpnumber || ''} ${record.device_id || ''} ${record.locality || ''} ${record.id || ''}`.toLowerCase().includes(needle));
    }
    return _.orderBy(list, [store.sort.key], [store.sort.dir > 0 ? 'asc' : 'desc']);
  };

  // DOM virtualization: only the rows near the viewport render; spacers keep the scroll height
  const OVERSCAN = 8;
  const visible = () => {
    const list = filtered();
    const start = Math.max(0, Math.floor(scroll().top / ROW_H) - OVERSCAN);
    const count = Math.ceil(scroll().height / ROW_H) + 2 * OVERSCAN;
    return {
      start,
      rows: list.slice(start, start + count),
      after: Math.max(0, list.length - start - count)
    };
  };
  const selected = () => wf().list.find(record => record.id === store.selected);
  const canImport = () => client.actionsFor({
    state: ''
  }).includes('IMPORT');

  // CSV of exactly what the table shows: the loaded rows after the search sieve, in the
  // current sort order and with the table's columns (rows not yet scrolled in are not exported)
  const exportCsv = () => {
    const columns = [{
      label: tr('event_dt'),
      value: record => fmtDtShort(record.event_dt)
    }, {
      label: tr('lpnumber'),
      value: record => record.lpnumber || ''
    }, {
      label: tr('speed_kmh'),
      value: record => {
        var _record$speed_kmh;
        return (_record$speed_kmh = record.speed_kmh) !== null && _record$speed_kmh !== void 0 ? _record$speed_kmh : '';
      }
    }, {
      label: tr('speed_limit'),
      value: record => {
        var _record$speed_limit;
        return (_record$speed_limit = record.speed_limit) !== null && _record$speed_limit !== void 0 ? _record$speed_limit : '';
      }
    }, {
      label: tr('type'),
      value: record => record.type || ''
    }, {
      label: tr('device_id'),
      value: record => record.device_id || ''
    }, {
      label: tr('locality'),
      value: record => record.locality || ''
    }, {
      label: tr('list.ImportedBy'),
      value: record => m.importerLabel(record)
    }, {
      label: tr('id_short'),
      value: record => record.id
    }, {
      label: tr('created_dt'),
      value: record => fmtDtShort(record.created_dt)
    }, {
      label: tr('state'),
      value: record => tr(`state.${record.state}`)
    }];
    const cell = value => `"${String(value !== null && value !== void 0 ? value : '').replace(/"/g, '""')}"`;
    const lines = [columns.map(column => cell(column.label)).join(';')];
    for (const record of filtered()) lines.push(columns.map(column => cell(column.value(record))).join(';'));
    // BOM so Excel decodes the UTF-8 diacritics; ';' matches the sk-locale list separator
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], {
      type: 'text/csv;charset=utf-8;'
    });
    // local date+time in the name, so repeated exports never overwrite each other
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    m.saveBlob(blob, `tzrecords_${stamp}.csv`);
  };

  // bootstrap: without a scrollbar there are no scroll events — keep pulling pages until
  // the viewport is filled (or the result set is exhausted); re-runs on every list change
  createEffect(() => {
    void store.window.height; // re-check when the module window resizes
    const loaded = wf().window_start + wf().list.length;
    if (!elWrap || wf().total <= loaded || wf().list.length === 0) return;
    if (elWrap.scrollHeight <= elWrap.clientHeight + ROW_H) client.loadMore();
  });

  // bidirectional windowed scroll: near the bottom pulls the next server page (evicting
  // scrolled-past rows), near the top re-fetches the previous page and keeps the position
  const onScroll = async e => {
    const el = e.currentTarget;
    setScroll({
      top: el.scrollTop,
      height: el.clientHeight
    });
    if (el.scrollHeight - el.scrollTop - el.clientHeight < ROW_H * 10) {
      client.loadMore();
    } else if (el.scrollTop < ROW_H * 5 && wf().window_start > 0) {
      const prepended = await client.loadPrevious();
      if (prepended > 0) el.scrollTop += prepended * ROW_H;
    }
  };
  return (() => {
    var _el$3 = _tmpl$9(),
      _el$4 = _el$3.firstChild,
      _el$5 = _el$4.firstChild,
      _el$6 = _el$5.nextSibling,
      _el$7 = _el$6.firstChild,
      _el$8 = _el$6.nextSibling,
      _el$10 = _el$4.nextSibling,
      _el$11 = _el$10.firstChild,
      _el$12 = _el$11.firstChild,
      _el$13 = _el$12.firstChild,
      _el$14 = _el$13.firstChild,
      _el$15 = _el$14.firstChild,
      _el$16 = _el$13.nextSibling,
      _el$23 = _el$10.nextSibling,
      _el$24 = _el$23.firstChild,
      _el$26 = _el$24.nextSibling,
      _el$25 = _el$26.nextSibling;
    _$insert(_el$5, () => tr('Title'));
    _$insert(_el$6, _$createComponent(For, {
      get each() {
        return m.listChips();
      },
      children: chip => (() => {
        var _el$27 = _tmpl$0();
        _el$27.$$click = () => m.setChip(chip);
        _$insert(_el$27, () => chip.label);
        _$effect(() => _el$27.classList.toggle("sel", !!m.chipSelected(chip)));
        return _el$27;
      })()
    }), _el$7);
    _$insert(_el$6, _$createComponent(FilterButton, {}), _el$7);
    _el$7.$$input = e => setStore('search', e.currentTarget.value);
    _el$8.$$click = exportCsv;
    _$insert(_el$8, () => tr('list.Export'));
    _$insert(_el$4, _$createComponent(Show, {
      get when() {
        return canImport();
      },
      get children() {
        return [(() => {
          var _el$9 = _tmpl$3();
          _el$9.$$click = () => m.openBulk();
          _$insert(_el$9, () => tr('bulk.Title'));
          return _el$9;
        })(), (() => {
          var _el$0 = _tmpl$4();
          _el$0.$$click = () => m.openWizard(null);
          _$insert(_el$0, () => tr('NewItem'));
          return _el$0;
        })()];
      }
    }), null);
    _$insert(_el$3, _$createComponent(FilterChips, {}), _el$10);
    _$insert(_el$3, _$createComponent(Show, {
      get when() {
        return wf().error;
      },
      get children() {
        var _el$1 = _tmpl$5();
        _$insert(_el$1, () => wf().error);
        return _el$1;
      }
    }), _el$10);
    _el$11.addEventListener("scroll", onScroll);
    var _ref$ = elWrap;
    typeof _ref$ === "function" ? _$use(_ref$, _el$11) : elWrap = _el$11;
    _$insert(_el$14, _$createComponent(Th, {
      key_: "event_dt",
      "class": "tzr-right tzr-col-dt"
    }), null);
    _$insert(_el$14, _$createComponent(Th, {
      key_: "lpnumber",
      "class": "tzr-lp-col"
    }), null);
    _$insert(_el$14, _$createComponent(Th, {
      key_: "speed_kmh",
      "class": "tzr-right tzr-col-speed"
    }), null);
    _$insert(_el$14, _$createComponent(Th, {
      key_: "type"
    }), null);
    _$insert(_el$14, _$createComponent(Th, {
      key_: "device_id"
    }), null);
    _$insert(_el$14, _$createComponent(Th, {
      key_: "locality"
    }), null);
    _$insert(_el$14, _$createComponent(Th, {
      key_: "import_user_id",
      label: "list.ImportedBy"
    }), null);
    _$insert(_el$14, _$createComponent(Th, {
      key_: "id",
      label: "id_short"
    }), null);
    _$insert(_el$14, _$createComponent(Th, {
      key_: "created_dt",
      "class": "tzr-right tzr-col-dt"
    }), null);
    _$insert(_el$14, _$createComponent(Th, {
      key_: "state"
    }), null);
    _$insert(_el$16, _$createComponent(Show, {
      get when() {
        return visible().start > 0;
      },
      get children() {
        var _el$17 = _tmpl$6(),
          _el$18 = _el$17.firstChild;
        _$effect(_$p => _$setStyleProperty(_el$18, "height", `${visible().start * ROW_H}px`));
        return _el$17;
      }
    }), null);
    _$insert(_el$16, _$createComponent(For, {
      get each() {
        return visible().rows;
      },
      children: record => (() => {
        var _el$28 = _tmpl$10(),
          _el$29 = _el$28.firstChild,
          _el$31 = _el$29.nextSibling,
          _el$32 = _el$31.nextSibling,
          _el$33 = _el$32.nextSibling,
          _el$34 = _el$33.nextSibling,
          _el$35 = _el$34.nextSibling,
          _el$36 = _el$35.nextSibling,
          _el$37 = _el$36.nextSibling,
          _el$38 = _el$37.nextSibling,
          _el$39 = _el$38.nextSibling,
          _el$40 = _el$39.nextSibling,
          _el$41 = _el$40.firstChild;
        _el$28.$$click = () => m.openRecord(store.selected === record.id ? '' : record.id);
        _$insert(_el$29, _$createComponent(Show, {
          get when() {
            return record.jpeg3;
          },
          get children() {
            var _el$30 = _tmpl$1();
            _$effect(() => _$setAttribute(_el$30, "src", record.jpeg3));
            return _el$30;
          }
        }));
        _$insert(_el$31, () => fmtDtShort(record.event_dt));
        _$insert(_el$32, () => record.lpnumber || '—');
        _$insert(_el$33, (() => {
          var _c$ = _$memo(() => record.speed_kmh != null);
          return () => {
            var _record$speed_limit2;
            return _c$() ? `${record.speed_kmh} / ${(_record$speed_limit2 = record.speed_limit) !== null && _record$speed_limit2 !== void 0 ? _record$speed_limit2 : '—'}` : '—';
          };
        })());
        _$insert(_el$34, () => record.type || '—');
        _$insert(_el$35, () => record.device_id || '—');
        _$insert(_el$36, () => record.locality || '—');
        _$insert(_el$37, () => m.importerLabel(record));
        _$insert(_el$38, () => record.id);
        _$insert(_el$39, () => fmtDtShort(record.created_dt));
        _$insert(_el$41, () => tr(`state.${record.state}`));
        _$effect(_p$ => {
          var _v$3 = !!(store.selected === record.id),
            _v$4 = m.importerTitle(record.import_user_id),
            _v$5 = `tzr-pill ${STATE_TONES[record.state] || ''}`;
          _v$3 !== _p$.e && _el$28.classList.toggle("sel", _p$.e = _v$3);
          _v$4 !== _p$.t && _$setAttribute(_el$37, "title", _p$.t = _v$4);
          _v$5 !== _p$.a && _$className(_el$41, _p$.a = _v$5);
          return _p$;
        }, {
          e: undefined,
          t: undefined,
          a: undefined
        });
        return _el$28;
      })()
    }), null);
    _$insert(_el$16, _$createComponent(Show, {
      get when() {
        return visible().after > 0;
      },
      get children() {
        var _el$19 = _tmpl$6(),
          _el$20 = _el$19.firstChild;
        _$effect(_$p => _$setStyleProperty(_el$20, "height", `${visible().after * ROW_H}px`));
        return _el$19;
      }
    }), null);
    _$insert(_el$11, _$createComponent(Show, {
      get when() {
        return _$memo(() => filtered().length === 0)() && !wf().loading;
      },
      get children() {
        var _el$21 = _tmpl$7();
        _$insert(_el$21, () => tr('list.Empty'));
        return _el$21;
      }
    }), null);
    _$insert(_el$11, _$createComponent(Show, {
      get when() {
        return wf().paging;
      },
      get children() {
        return _tmpl$8();
      }
    }), null);
    _$insert(_el$10, _$createComponent(Drawer, {
      get open() {
        return !!selected();
      },
      onClose: () => setStore('selected', ''),
      get children() {
        return _$createComponent(ReadPanel, {
          get record() {
            return selected();
          }
        });
      }
    }), null);
    _$insert(_el$23, () => wf().window_start + 1, _el$24);
    _$insert(_el$23, () => wf().window_start + wf().list.length, _el$26);
    _$insert(_el$23, () => Math.max(wf().total, wf().list.length), null);
    _$effect(_p$ => {
      var _v$ = t('form.search'),
        _v$2 = filtered().length === 0;
      _v$ !== _p$.e && _$setAttribute(_el$7, "placeholder", _p$.e = _v$);
      _v$2 !== _p$.t && (_el$8.disabled = _p$.t = _v$2);
      return _p$;
    }, {
      e: undefined,
      t: undefined
    });
    _$effect(() => _el$7.value = store.search);
    return _el$3;
  })();
}
_$delegateEvents(["click", "input"]);